/**
 * Agent tools over the local CMS store.
 *
 * Mirrors the Zendesk tool surface (list/get/create/update/delete/move/publish/
 * translate/search/audit) so the same agent loop drives either backend, and adds
 * operations Zendesk cannot express — nested sections and whole-tree reads.
 *
 * All mutations go through `cms/store.ts`, i.e. localStorage only. No network.
 */

import { tool } from "ai"
import { z } from "zod"
import * as db from "@/pages/dev/smart-cms/articles/store"
import { type KBStore, nodeTitle, articleState, localesByState } from "@/pages/dev/smart-cms/articles/types"
import { withQueue, toMd } from "@/pages/dev/smart-cms/shared/toolQueue"
import { describeLanguage, normalizeLocale } from "@/pages/dev/smart-cms/shared/languages"
import { SITE_LOCALE_CODES } from "@/pages/dev/smart-cms/shared/siteLanguages"
import { type ChangeEntry, type NodeVersion, describeEntry } from "@/pages/dev/smart-cms/articles/history"

/**
 * Tools need to read the latest store and publish updates back to React.
 * Passing accessors (rather than a snapshot) avoids stale-state bugs across the
 * many sequential tool calls in a long agent run.
 */
export interface CMSToolContext {
  getStore: () => KBStore
  setStore: (next: KBStore) => void
  locale: string
  onNodeFocused?: (id: string) => void
  onChanged?: () => void
  /** Records a change so it appears in the audit log and can be reverted. */
  record?: (args: {
    prevStore: KBStore
    nextStore?: KBStore
    action: "create" | "update" | "delete" | "publish" | "labels" | "move" | "translate"
    actor: "agent" | "user"
    nodeId: string
    summary: string
    locale?: string
  }) => void
  /** Reads the change log, newest last. */
  getHistory?: () => ChangeEntry[]
  /** Reverts one logged change by id. */
  revert?: (entryId: string) => { ok: true; description: string } | { ok: false; error: string }
  /** Version timeline for one node, newest first. */
  versionsOf?: (nodeId: string) => NodeVersion[]
  /** Restores a node to one of its recorded versions. */
  restoreNodeVersion?: (nodeId: string, entryId: string) => { ok: true; description: string } | { ok: false; error: string }
  /**
   * HITL gate. Resolves true when the operation is approved.
   *
   * `bodyBefore`/`bodyAfter` are MARKDOWN, not HTML — the review card renders
   * them through ToMarkdown, so HTML would show up as literal tags.
   */
  onRequestApproval?: (req: {
    summary: string
    titleBefore?: string
    titleAfter?: string
    bodyBefore?: string
    bodyAfter?: string
    locale?: string
  }) => Promise<boolean>
  /** When true, destructive ops skip the approval prompt (used by long jobs). */
  autoAccept?: boolean
}

const ok = (data: any) => data
const fail = (message: string) => ({ error: message })

function tryDo<T>(fn: () => T): T | { error: string } {
  try { return fn() } catch (e: any) { return fail(e?.message || String(e)) }
}

export function makeCMSTools(ctx: CMSToolContext, hitlQueue?: { current: Promise<void> }) {
  const commit = (next: KBStore) => { ctx.setStore(next); ctx.onChanged?.() }

  /**
   * Commits a change AND records it in the audit log, so every agent mutation is
   * traceable and revertable. Deletes must pass `recordBefore: true` because the
   * snapshot has to be taken while the subtree still exists.
   */
  const commitTracked = (args: {
    prevStore: KBStore
    nextStore: KBStore
    action: "create" | "update" | "delete" | "publish" | "labels" | "move" | "translate"
    nodeId: string
    summary: string
    locale?: string
  }) => {
    if (args.action === "delete") {
      // Record first: collectSubtree needs the pre-delete store.
      ctx.record?.({
        prevStore: args.prevStore, action: "delete", actor: "agent",
        nodeId: args.nodeId, summary: args.summary, locale: args.locale,
      })
      commit(args.nextStore)
      return
    }
    commit(args.nextStore)
    ctx.record?.({
      prevStore: args.prevStore, nextStore: args.nextStore, action: args.action,
      actor: "agent", nodeId: args.nodeId, summary: args.summary, locale: args.locale,
    })
  }

  /**
   * Human-in-the-loop gate.
   *
   * The model routinely emits several destructive calls in a single step (e.g.
   * "delete everything" → three cms_delete calls), and the AI SDK executes the
   * calls in one step concurrently. The approval UI has a single slot, so
   * without serialization the later prompts overwrite the earlier ones and
   * those tool calls never settle — the stream appears to hang.
   *
   * Routing every approval through one shared queue means prompt N+1 is only
   * raised after N resolves, matching the Zendesk tools' behaviour.
   */
  const gate = async (req: {
    summary: string
    titleBefore?: string
    titleAfter?: string
    bodyBefore?: string
    bodyAfter?: string
    locale?: string
  }): Promise<boolean> => {
    if (ctx.autoAccept) return true
    if (!ctx.onRequestApproval) return true
    const ask = () => ctx.onRequestApproval!(req)
    return hitlQueue ? withQueue(hitlQueue, ask) : ask()
  }

  const L = () => ctx.locale
  const title = (id: string) => {
    const s = ctx.getStore()
    const n = db.getNode(s, id)
    return n ? nodeTitle(n, L(), s.defaultLocale) : id
  }

  return {
    // ── Read ────────────────────────────────────────────────────────────────
    cms_get_tree: tool({
      description:
        "Read the entire knowledge base structure as a nested tree (categories → sections → nested sections → articles). " +
        "Use this FIRST to orient yourself before making changes. Returns titles, ids, article counts and publish state, but not article bodies.",
      parameters: z.object({
        locale: z.string().nullable().describe("Locale for titles. Defaults to the active locale."),
      }),
      execute: async ({ locale }) => tryDo(() => {
        const s = ctx.getStore()
        const loc = locale || L()
        const t = (id: string) => {
          const n = db.getNode(s, id)!
          return nodeTitle(n, loc, s.defaultLocale)
        }
        const walkSections = (categoryId: string, parentSectionId: string | null): any[] =>
          db.childSections(s, { categoryId, parentSectionId }).map(sec => ({
            id: sec.id,
            title: t(sec.id),
            locales: Object.keys(sec.translations),
            sections: walkSections(categoryId, sec.id),
            articles: db.articlesIn(s, sec.id).map(a => ({
              id: a.id, title: t(a.id),
              state: articleState(a, loc),
              publishedLocales: localesByState(a).published,
              labels: a.labels, locales: Object.keys(a.translations),
            })),
          }))
        return ok({
          locale: loc,
          defaultLocale: s.defaultLocale,
          locales: s.locales,
          stats: db.stats(s),
          categories: db.allCategories(s).map(c => ({
            id: c.id, title: t(c.id), locales: Object.keys(c.translations),
            sections: walkSections(c.id, null),
          })),
        })
      }),
    }),

    cms_get_article: tool({
      description: "Read one article in full, including its HTML body, for a given locale.",
      parameters: z.object({
        id: z.string(),
        locale: z.string().nullable().describe("Locale to read, or null for the active locale"),
      }),
      execute: async ({ id, locale }) => tryDo(() => {
        const s = ctx.getStore()
        const a = s.articles[id]
        if (!a) return fail(`No such article: ${id}`)
        const loc = locale || L()
        const tr = a.translations[loc] ?? a.translations[a.sourceLocale]
        ctx.onNodeFocused?.(id)
        const grouped = localesByState(a)
        return ok({
          id: a.id,
          path: db.pathOf(s, id, loc),
          /** State of the locale being read; see publishedLocales/draftLocales for the rest. */
          state: articleState(a, loc),
          publishedLocales: grouped.published,
          draftLocales: grouped.draft,
          labels: a.labels,
          sectionId: a.sectionId,
          sourceRef: a.sourceRef,
          locale: tr?.locale,
          title: tr?.title,
          body: tr?.body,
          reviewed: tr?.reviewed,
          availableLocales: Object.keys(a.translations),
        })
      }),
    }),

    cms_search: tool({
      description:
        "Keyword search across every category, section and article, in all locales. Results are ranked — title and label matches outrank body matches, " +
        "and whole-phrase matches rank highest. Partial words work ('hous' finds 'housing'). " +
        "Narrow results with the filters: kind, locale, state, labels, or withinId to search inside one category/section subtree. " +
        "This is the main way to navigate: prefer it over reading the whole tree when looking for something specific.",
      parameters: z.object({
        query: z.string().describe("Words to look for. Multiple words are matched individually and as a phrase."),
        kind: z.enum(["category", "section", "article"]).nullable().describe("Only this node type, or null for all"),
        locale: z.string().nullable().describe("Only this locale, or null to search all"),
        state: z.enum(["draft", "published"]).nullable().describe("Articles in this state only, or null for any"),
        labels: z.array(z.string()).nullable().describe("Only articles carrying ALL of these labels, or null"),
        withinId: z.string().nullable().describe("Only content inside this category or section (at any depth), or null for the whole KB"),
        requireAll: z.boolean().nullable().describe("Require every query word to match. Default false (any word matches)."),
        limit: z.number().nullable().describe("Max results (default 25)"),
      }),
      execute: async ({ query, kind, locale, state, labels, withinId, requireAll, limit }) => tryDo(() => {
        const s = ctx.getStore()
        const results = db.searchNodes(s, query, {
          limit: limit ?? 25,
          kind: kind ?? undefined,
          locale: locale ?? undefined,
          state: state ?? undefined,
          labels: labels ?? undefined,
          withinId: withinId ?? undefined,
          requireAll: requireAll ?? undefined,
        })
        return ok({
          query,
          resultCount: results.length,
          results: results.map(r => ({
            id: r.node.id,
            kind: r.node.kind,
            locale: r.locale,
            path: db.pathOf(s, r.node.id, r.locale),
            score: r.score,
            matchedTerms: r.matchedTerms,
            matchedIn: r.matchedIn,
            state: r.node.kind === "article" ? articleState(r.node as any, r.locale) : undefined,
            labels: r.node.kind === "article" ? (r.node as any).labels : undefined,
            snippet: r.snippet,
          })),
        })
      }),
    }),

    cms_list_children: tool({
      description:
        "List the direct children of a category or section — its subsections and articles — without loading the whole tree. " +
        "Pass null to list top-level categories. Use this to walk the KB one level at a time.",
      parameters: z.object({
        parentId: z.string().nullable().describe("Category or section id, or null for top-level categories"),
      }),
      execute: async ({ parentId }) => tryDo(() => {
        const s = ctx.getStore()
        const loc = L()
        const t = (id: string) => {
          const n = db.getNode(s, id)
          return n ? nodeTitle(n, loc, s.defaultLocale) : "(missing)"
        }

        if (!parentId) {
          return ok({
            level: "root",
            categories: db.allCategories(s).map(c => ({
              id: c.id, title: t(c.id),
              sectionCount: db.childSections(s, { categoryId: c.id, parentSectionId: null }).length,
            })),
          })
        }

        const node = db.getNode(s, parentId)
        if (!node) return fail(`No such node: ${parentId}`)
        if (node.kind === "article") return fail("Articles have no children.")

        const categoryId = node.kind === "category" ? node.id : (node as any).categoryId
        const parentSectionId = node.kind === "section" ? node.id : null

        return ok({
          parentId,
          parentKind: node.kind,
          path: db.pathOf(s, parentId, loc),
          sections: db.childSections(s, { categoryId, parentSectionId }).map(x => ({
            id: x.id, title: t(x.id),
            articleCount: db.articlesIn(s, x.id).length,
            subsectionCount: db.childSections(s, { categoryId, parentSectionId: x.id }).length,
          })),
          articles: parentSectionId
            ? db.articlesIn(s, parentSectionId).map(a => ({
                id: a.id, title: t(a.id), state: articleState(a, loc), labels: a.labels,
              }))
            : [],
        })
      }),
    }),

    cms_list_labels: tool({
      description:
        "List every label in use, with how many articles carry each. Use this to discover the KB's tagging vocabulary before filtering a search by label.",
      parameters: z.object({}),
      execute: async () => tryDo(() => {
        const s = ctx.getStore()
        const counts = new Map<string, number>()
        for (const a of Object.values(s.articles)) {
          for (const l of a.labels) counts.set(l, (counts.get(l) ?? 0) + 1)
        }
        return ok({
          labelCount: counts.size,
          labels: [...counts.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([label, articleCount]) => ({ label, articleCount })),
        })
      }),
    }),

    cms_find_articles: tool({
      description:
        "List articles by attribute rather than by keyword — filter on state, labels, locale, missing-translation, or subtree, and sort by recency or title. " +
        "Use this for questions like 'which articles are still drafts', 'what's untranslated in es-MX', or 'what changed most recently'.",
      parameters: z.object({
        state: z.enum(["draft", "published"]).nullable().describe("Only this state, or null for any"),
        labels: z.array(z.string()).nullable().describe("Only articles with ALL these labels, or null"),
        missingLocale: z.string().nullable().describe("Only articles with NO translation for this locale, or null"),
        withinId: z.string().nullable().describe("Only articles inside this category/section subtree, or null"),
        sort: z.enum(["recent", "oldest", "title"]).nullable().describe("Sort order. Default 'recent'."),
        limit: z.number().nullable().describe("Max results (default 50)"),
      }),
      execute: async ({ state, labels, missingLocale, withinId, sort, limit }) => tryDo(() => {
        const s = ctx.getStore()
        const loc = L()
        let rows = Object.values(s.articles)

        // Per-locale state: match if the active locale (or any locale) is in it.
        if (state) rows = rows.filter(a => Object.keys(a.translations).some(l => articleState(a, l) === state))
        if (labels?.length) {
          rows = rows.filter(a => {
            const have = new Set(a.labels.map(l => l.toLowerCase()))
            return labels.every(l => have.has(l.toLowerCase()))
          })
        }
        if (missingLocale) rows = rows.filter(a => !a.translations[missingLocale])
        if (withinId) {
          // Keep only articles whose ancestry includes withinId.
          rows = rows.filter(a => {
            if (a.sectionId === withinId) return true
            let sid: string | null = a.sectionId
            while (sid && s.sections[sid]) {
              if (sid === withinId) return true
              if (s.sections[sid].categoryId === withinId) return true
              sid = s.sections[sid].parentSectionId
            }
            return false
          })
        }

        const order = sort ?? "recent"
        rows = [...rows].sort((a, b) =>
          order === "title" ? nodeTitle(a, loc, s.defaultLocale).localeCompare(nodeTitle(b, loc, s.defaultLocale))
          : order === "oldest" ? a.updatedAt - b.updatedAt
          : b.updatedAt - a.updatedAt
        )

        return ok({
          matchCount: rows.length,
          articles: rows.slice(0, limit ?? 50).map(a => ({
            id: a.id,
            title: nodeTitle(a, loc, s.defaultLocale),
            path: db.pathOf(s, a.id, loc),
            state: articleState(a, loc),
            publishedLocales: localesByState(a).published,
            labels: a.labels,
            locales: Object.keys(a.translations),
            updatedAt: new Date(a.updatedAt).toISOString(),
          })),
        })
      }),
    }),

    // ── Create ──────────────────────────────────────────────────────────────
    cms_create_category: tool({
      description: "Create a top-level category.",
      parameters: z.object({
        title: z.string(),
        description: z.string().nullable().describe("Optional description, or null"),
        locale: z.string().nullable().describe("Locale to write, or null for the active locale"),
      }),
      execute: async ({ title: t, description, locale }) => tryDo(() => {
        const prev = ctx.getStore()
        const { store, id } = db.createCategory(prev, t, locale || L(), description || "")
        commitTracked({ prevStore: prev, nextStore: store, action: "create", nodeId: id, summary: `Created category "${t}"`, locale: locale || L() })
        return ok({ id, title: t })
      }),
    }),

    cms_create_section: tool({
      description:
        "Create a section inside a category, or nested inside another section by passing parentSectionId. " +
        "Nested sections are supported to arbitrary depth.",
      parameters: z.object({
        title: z.string(),
        categoryId: z.string().describe("Owning category. Required even for nested sections."),
        parentSectionId: z.string().nullable().describe("Parent section id, for nesting. Omit for a top-level section."),
        description: z.string().nullable().describe("Optional description, or null"),
        locale: z.string().nullable().describe("Locale to write, or null for the active locale"),
      }),
      execute: async ({ title: t, categoryId, parentSectionId, description, locale }) => tryDo(() => {
        const prev = ctx.getStore()
        const { store, id } = db.createSection(prev, {
          title: t, categoryId, parentSectionId: parentSectionId ?? null,
          description: description ?? undefined, locale: locale || L(),
        })
        commitTracked({ prevStore: prev, nextStore: store, action: "create", nodeId: id, summary: `Created section "${t}"`, locale: locale || L() })
        return ok({ id, title: t, parentSectionId: parentSectionId ?? null })
      }),
    }),

    cms_create_article: tool({
      description:
        "Create an article in a section. Body is HTML. Created as a draft unless state is set to 'published'. " +
        "Set sourceRef to the originating filename when building from source documents, for auditability.",
      parameters: z.object({
        title: z.string(),
        body: z.string().describe("HTML content."),
        sectionId: z.string(),
        state: z.enum(["draft", "published"]).nullable().describe("Publish state, or null for draft"),
        labels: z.array(z.string()).nullable().describe("Labels to apply, or null for none"),
        locale: z.string().nullable().describe("Locale to write, or null for the active locale"),
        sourceRef: z.string().nullable().describe("Originating filename, or null"),
      }),
      execute: async ({ title: t, body, sectionId, state, labels, locale, sourceRef }) => tryDo(() => {
        const prev = ctx.getStore()
        const { store, id } = db.createArticle(prev, {
          title: t, body, sectionId,
          state: state ?? "draft",
          labels: labels ?? undefined,
          locale: locale || L(),
          sourceRef: sourceRef ?? undefined,
        })
        commitTracked({ prevStore: prev, nextStore: store, action: "create", nodeId: id, summary: `Created article "${t}"`, locale: locale || L() })
        ctx.onNodeFocused?.(id)
        return ok({ id, title: t, state })
      }),
    }),

    // ── Update ──────────────────────────────────────────────────────────────
    cms_update_content: tool({
      description:
        "Update the title, body and/or description of any node (category, section or article) for one locale. " +
        "Use this to edit article content, rename things, or fill in a translation.",
      parameters: z.object({
        id: z.string(),
        locale: z.string().nullable().describe("Locale to write, or null for the active locale"),
        title: z.string().nullable().describe("New title, or null to keep current"),
        body: z.string().nullable().describe("New HTML body, or null to keep current. Articles only."),
        description: z.string().nullable().describe("New description, or null to keep current"),
        reviewed: z.boolean().nullable().describe("Mark this locale as human-reviewed."),
      }),
      execute: async ({ id, locale, ...raw }) => {
        const s0 = ctx.getStore()
        const node = db.getNode(s0, id)
        if (!node) return fail(`No such node: ${id}`)
        const loc = locale || L()

        // Fields are declared nullable (not optional) so the provider's strict
        // function schema accepts them. A null therefore means "leave alone" —
        // strip those out so setTranslation keeps the existing value.
        const patch: Record<string, any> = {}
        for (const [k, v] of Object.entries(raw)) if (v !== null && v !== undefined) patch[k] = v

        if (Object.keys(patch).length === 0) return fail("Nothing to update — provide at least one field.")

        // Diff against what's actually there, converted to markdown so the
        // review card renders prose rather than raw HTML tags.
        const existing = node.translations[loc]
        const currentTitle = existing?.title ?? ""
        const approved = await gate({
          summary: `Update ${node.kind} "${nodeTitle(node, loc, s0.defaultLocale)}"`,
          titleBefore: currentTitle,
          titleAfter: typeof patch.title === "string" ? patch.title : currentTitle,
          bodyBefore: toMd(existing?.body ?? ""),
          bodyAfter: toMd(typeof patch.body === "string" ? patch.body : (existing?.body ?? "")),
          locale: loc,
        })
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          const prev = ctx.getStore()
          const changed = Object.keys(patch).join(", ")
          commitTracked({
            prevStore: prev, nextStore: db.setTranslation(prev, id, loc, patch),
            action: "update", nodeId: id, locale: loc,
            summary: `Updated ${changed} on "${nodeTitle(node, loc, prev.defaultLocale)}" (${loc})`,
          })
          ctx.onNodeFocused?.(id)
          return ok({ id, locale: loc, updated: Object.keys(patch) })
        })
      },
    }),

    cms_publish_article: tool({
      description:
        "Set the publish state of ONE locale of an article. Publish state is per-locale: an English article can be live " +
        "while its Spanish translation is still a draft. Pass the locale you mean. Use allLocales only when the user " +
        "explicitly asks to publish or unpublish every translation at once.",
      parameters: z.object({
        id: z.string(),
        state: z.enum(["draft", "published"]).nullable().describe("Target state. Defaults to 'published'."),
        locale: z.string().nullable().describe("Which locale to change, or null for the active locale"),
        allLocales: z.boolean().nullable().describe("Apply to every existing translation. Default false."),
      }),
      execute: async ({ id, state, locale, allLocales }) => tryDo(() => {
        const nextState = state ?? "published"
        const loc = locale || L()
        const prev = ctx.getStore()
        const article = prev.articles[id]
        if (!article) return fail(`No such article: ${id}`)

        const next = db.setArticleState(prev, id, nextState, {
          locale: loc, allLocales: allLocales ?? false,
        })
        const scope = allLocales ? "all locales" : loc
        commitTracked({
          prevStore: prev, nextStore: next, action: "publish", nodeId: id, locale: loc,
          summary: `Set ${scope} to ${nextState}`,
        })
        const after = next.articles[id]
        const grouped = localesByState(after)
        return ok({ id, state: nextState, scope, published: grouped.published, drafts: grouped.draft })
      }),
    }),

    cms_set_labels: tool({
      description: "Replace an article's labels with the given list.",
      parameters: z.object({ id: z.string(), labels: z.array(z.string()) }),
      execute: async ({ id, labels }) => tryDo(() => {
        const prev = ctx.getStore()
        commitTracked({
          prevStore: prev, nextStore: db.setArticleLabels(prev, id, labels),
          action: "labels", nodeId: id, summary: `Set labels: ${labels.join(", ") || "(none)"}`,
        })
        return ok({ id, labels })
      }),
    }),

    cms_move_article: tool({
      description: "Move an article into a different section.",
      parameters: z.object({ id: z.string(), sectionId: z.string() }),
      execute: async ({ id, sectionId }) => tryDo(() => {
        const prev = ctx.getStore()
        commitTracked({
          prevStore: prev, nextStore: db.moveArticle(prev, id, sectionId),
          action: "move", nodeId: id, summary: `Moved article to section ${sectionId}`,
        })
        return ok({ id, sectionId })
      }),
    }),

    cms_move_section: tool({
      description:
        "Reparent a section — into a different category, or nest it under another section (or pass parentSectionId null to make it top-level). " +
        "Cycles are rejected.",
      parameters: z.object({
        id: z.string(),
        categoryId: z.string().nullable().describe("Move to this category, or null to keep the current one"),
        parentSectionId: z.string().nullable().describe("Nest under this section, or null to make it top-level"),
      }),
      execute: async ({ id, categoryId, parentSectionId }) => tryDo(() => {
        // parentSectionId null is meaningful here ("make it top-level") and is
        // passed through. categoryId null just means "don't change the category".
        const prev = ctx.getStore()
        commitTracked({
          prevStore: prev,
          nextStore: db.moveSection(prev, id, { categoryId: categoryId ?? undefined, parentSectionId }),
          action: "move", nodeId: id,
          summary: `Reparented section (category=${categoryId ?? "unchanged"}, parent=${parentSectionId ?? "top-level"})`,
        })
        return ok({ id, categoryId, parentSectionId })
      }),
    }),

    // ── Delete ──────────────────────────────────────────────────────────────
    cms_delete: tool({
      description:
        "Delete a category, section or article. Deleting a category or section also deletes everything nested beneath it. " +
        "Requires reviewer approval unless auto-accept is on.",
      parameters: z.object({ id: z.string() }),
      execute: async ({ id }) => {
        const s0 = ctx.getStore()
        const node = db.getNode(s0, id)
        if (!node) return fail(`No such node: ${id}`)
        const label = `${node.kind} "${nodeTitle(node, L(), s0.defaultLocale)}"`
        // Show what will be destroyed: the path, plus the subtree counts, since
        // deletes cascade and the agent may be asking for more than it realises.
        const counts = node.kind === "article"
          ? "1 article"
          : (() => {
              const probe = db.deleteNode(s0, id).deleted
              return `${probe.categories} categor${probe.categories === 1 ? "y" : "ies"}, ${probe.sections} section(s), ${probe.articles} article(s)`
            })()
        const approved = await gate({
          summary: `Delete ${label} and all of its contents`,
          titleBefore: db.pathOf(s0, id, L()),
          titleAfter: "(deleted)",
          bodyBefore: `**Path:** ${db.pathOf(s0, id, L())}\n\n**This will permanently remove:** ${counts}`,
          bodyAfter: "",
          locale: L(),
        })
        if (!approved) return fail("Rejected by reviewer.")
        return tryDo(() => {
          const prev = ctx.getStore()
          const { store, deleted } = db.deleteNode(prev, id)
          commitTracked({
            prevStore: prev, nextStore: store, action: "delete", nodeId: id, locale: L(),
            summary: `Deleted ${label} — removed ${counts}`,
          })
          return ok({ id, deleted, note: "Recorded in the change log; use cms_revert_change to undo." })
        })
      },
    }),

    // ── Change log ──────────────────────────────────────────────────────────
    cms_history: tool({
      description:
        "Read the change log: every create, update, delete, publish, label, move and translate, by you or the user, newest first. " +
        "Use this when asked what changed, or to find the entry id to undo. Each entry reports whether it is still revertable.",
      parameters: z.object({
        nodeId: z.string().nullable().describe("Only changes affecting this node, or null for all"),
        action: z.enum(["create", "update", "delete", "publish", "labels", "move", "translate", "revert"])
          .nullable().describe("Filter to one action type, or null for all"),
        limit: z.number().nullable().describe("Max entries (default 30)"),
      }),
      execute: async ({ nodeId, action, limit }) => tryDo(() => {
        const all = ctx.getHistory?.() ?? []
        let rows = [...all].reverse() // newest first
        if (nodeId) rows = rows.filter(e => e.nodeId === nodeId)
        if (action) rows = rows.filter(e => e.action === action)
        return ok({
          total: all.length,
          shown: Math.min(rows.length, limit ?? 30),
          entries: rows.slice(0, limit ?? 30).map(describeEntry),
        })
      }),
    }),

    cms_article_versions: tool({
      description:
        "List the version timeline for one article (or any node): every recorded state, newest first, with who made each change and when. " +
        "Use this when the user asks about an article's edit history or wants to restore an earlier version.",
      parameters: z.object({ nodeId: z.string() }),
      execute: async ({ nodeId }) => tryDo(() => {
        const s = ctx.getStore()
        const node = db.getNode(s, nodeId)
        if (!node) return fail(`No such node: ${nodeId}`)
        const versions = ctx.versionsOf?.(nodeId) ?? []
        return ok({
          nodeId,
          path: db.pathOf(s, nodeId, L()),
          versionCount: versions.length,
          versions: versions.map(v => {
            const tr = v.node?.translations[L()] ?? (v.node ? Object.values(v.node.translations)[0] : undefined)
            return {
              entryId: v.entryId,
              at: new Date(v.at).toISOString(),
              action: v.action,
              author: v.author,
              actor: v.actor,
              summary: v.summary,
              isPriorState: !!v.isPriorState,
              title: tr?.title,
              bodyPreview: tr ? db.stripHtml(tr.body).slice(0, 200) : undefined,
              restorable: !!v.node,
            }
          }),
        })
      }),
    }),

    cms_restore_version: tool({
      description:
        "Restore an article (or node) to one of its earlier recorded versions, identified by the entryId from cms_article_versions. " +
        "The restore is itself logged, so it can be undone too. Describe which version you are restoring — date and author — before calling.",
      parameters: z.object({
        nodeId: z.string(),
        entryId: z.string().describe("entryId from cms_article_versions"),
      }),
      execute: async ({ nodeId, entryId }) => {
        if (!ctx.restoreNodeVersion) return fail("Version restore is unavailable.")
        const versions = ctx.versionsOf?.(nodeId) ?? []
        const target = versions.find(v => v.entryId === entryId)
        if (!target) return fail(`No version ${entryId} for node ${nodeId}`)
        if (!target.node) return fail("That version's snapshot was trimmed and can't be restored.")

        const s0 = ctx.getStore()
        const currentTr = db.getNode(s0, nodeId)?.translations[L()]
        const targetTr = target.node.translations[L()] ?? Object.values(target.node.translations)[0]

        const approved = await gate({
          summary: `Restore to the ${new Date(target.at).toLocaleString()} version by ${target.author}`,
          titleBefore: currentTr?.title ?? "",
          titleAfter: targetTr?.title ?? "",
          bodyBefore: toMd(currentTr?.body ?? ""),
          bodyAfter: toMd(targetTr?.body ?? ""),
          locale: L(),
        })
        if (!approved) return fail("Rejected by reviewer.")

        const result = ctx.restoreNodeVersion(nodeId, entryId)
        if (result.ok === false) return fail(result.error)
        ctx.onChanged?.()
        ctx.onNodeFocused?.(nodeId)
        return ok({ nodeId, entryId, description: result.description })
      },
    }),

    cms_revert_change: tool({
      description:
        "Undo one logged change by its id, restoring the affected content to how it was immediately before. " +
        "Reverting a delete restores the node and everything that was nested inside it. " +
        "Find ids with cms_history. Always tell the user exactly what you are about to undo before calling this.",
      parameters: z.object({ entryId: z.string().describe("Entry id from cms_history") }),
      execute: async ({ entryId }) => {
        const entries = ctx.getHistory?.() ?? []
        const entry = entries.find(e => e.id === entryId)
        if (!entry) return fail(`No history entry with id ${entryId}`)
        if (!ctx.revert) return fail("Revert is unavailable.")

        const approved = await gate({
          summary: `Undo: ${entry.summary}`,
          titleBefore: entry.path,
          titleAfter: entry.path,
          bodyBefore: `**Undoing:** ${entry.summary}\n\n**Recorded:** ${new Date(entry.at).toLocaleString()}\n\n**Path:** ${entry.path}`,
          bodyAfter: "",
          locale: entry.locale ?? L(),
        })
        if (!approved) return fail("Rejected by reviewer.")

        const result = ctx.revert(entryId)
        if (result.ok === false) return fail(result.error)
        ctx.onChanged?.()
        return ok({ entryId, reverted: true, description: result.description })
      },
    }),

    // ── Localization ────────────────────────────────────────────────────────
    cms_list_locales: tool({
      description: "List locales configured for this knowledge base.",
      parameters: z.object({}),
      execute: async () => tryDo(() => {
        const s = ctx.getStore()
        return ok({ locales: s.locales, defaultLocale: s.defaultLocale })
      }),
    }),

    cms_add_locale: tool({
      description: `Add a language to Articles so content can be translated into it. Only these codes are accepted, because they're the ones the Signpost sites can show: ${SITE_LOCALE_CODES.join(", ")}. The Service Map keeps its own list: add it there too with svc_add_locale.`,
      parameters: z.object({ locale: z.string() }),
      execute: async ({ locale }) => tryDo(() => {
        const r = normalizeLocale(locale)
        if (r.error) return fail(r.error)
        commit(db.ensureLocale(ctx.getStore(), r.code))
        return ok({ locale: r.code, name: describeLanguage(r.code).name })
      }),
    }),

    cms_missing_translations: tool({
      description:
        "Report every node missing a translation for the given locale. Use this to plan localization work, then fill each gap with cms_update_content.",
      parameters: z.object({
        locale: z.string(),
        limit: z.number().nullable().describe("Max rows to list (default 100)"),
      }),
      execute: async ({ locale, limit }) => tryDo(() => {
        const s = ctx.getStore()
        const missing: any[] = []
        const consider = (n: any) => {
          if (!n.translations[locale]) {
            missing.push({ id: n.id, kind: n.kind, path: db.pathOf(s, n.id, s.defaultLocale) })
          }
        }
        Object.values(s.categories).forEach(consider)
        Object.values(s.sections).forEach(consider)
        Object.values(s.articles).forEach(consider)
        return ok({ locale, missingCount: missing.length, missing: missing.slice(0, limit ?? 100) })
      }),
    }),

    // ── Audit ───────────────────────────────────────────────────────────────
    cms_audit: tool({
      description:
        "Quality audit of the whole KB: empty sections, thin articles (<200 chars), drafts, articles with no labels, " +
        "and untranslated nodes per locale. Use to decide what maintenance work to do next.",
      parameters: z.object({ thinCharThreshold: z.number().nullable().describe("Chars below which an article counts as thin (default 200)") }),
      execute: async ({ thinCharThreshold }) => tryDo(() => {
        const s = ctx.getStore()
        const threshold = thinCharThreshold ?? 200
        const articles = Object.values(s.articles)
        const thin = articles.filter(a => {
          const tr = a.translations[a.sourceLocale]
          return db.stripHtml(tr?.body || "").length < threshold
        })
        const emptySections = Object.values(s.sections).filter(sec =>
          db.articlesIn(s, sec.id).length === 0 && db.childSections(s, { parentSectionId: sec.id }).length === 0
        )
        const perLocale = s.locales.map(loc => {
          const total = Object.keys(s.categories).length + Object.keys(s.sections).length + articles.length
          let translated = 0
          const count = (n: any) => { if (n.translations[loc]) translated++ }
          Object.values(s.categories).forEach(count)
          Object.values(s.sections).forEach(count)
          articles.forEach(count)
          return { locale: loc, translated, total, missing: total - translated }
        })
        return ok({
          stats: db.stats(s),
          thinArticles: thin.map(a => ({ id: a.id, path: db.pathOf(s, a.id, s.defaultLocale) })),
          emptySections: emptySections.map(x => ({ id: x.id, path: db.pathOf(s, x.id, s.defaultLocale) })),
          drafts: articles
            .filter(a => localesByState(a).draft.length > 0)
            .map(a => ({ id: a.id, path: db.pathOf(s, a.id, s.defaultLocale), draftLocales: localesByState(a).draft })),
          unlabeled: articles.filter(a => a.labels.length === 0).map(a => ({ id: a.id, path: db.pathOf(s, a.id, s.defaultLocale) })),
          localeCoverage: perLocale,
        })
      }),
    }),
  }
}

export type CMSTools = ReturnType<typeof makeCMSTools>

export const CMS_TOOL_LABELS: Record<string, string> = {
  cms_get_tree: "Read KB Tree",
  cms_get_article: "Get Article",
  cms_search: "Search KB",
  cms_list_children: "List Children",
  cms_list_labels: "List Labels",
  cms_find_articles: "Find Articles",
  cms_create_category: "Create Category",
  cms_create_section: "Create Section",
  cms_create_article: "Create Article",
  cms_update_content: "Update Content",
  cms_publish_article: "Publish Article",
  cms_set_labels: "Set Labels",
  cms_move_article: "Move Article",
  cms_move_section: "Move Section",
  cms_delete: "Delete Node",
  cms_list_locales: "List Locales",
  cms_add_locale: "Add Locale",
  cms_missing_translations: "Find Missing Translations",
  cms_audit: "Audit KB",
  cms_history: "Read Change Log",
  cms_revert_change: "Undo Change",
  cms_article_versions: "Article Versions",
  cms_restore_version: "Restore Version",
}
